import {
  BACKWARDS_BOGO_SUBEXPERIENCES,
  DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCES,
  EXPERIENCE_TAGS,
  FORWARDS_BOGO_SUBEXPERIENCES,
} from './constants';

export const noop = () => { };

/**
 * @param {string} url
 * @returns {string}
 */
export const sanitizeImageUrl = (url = '') => url.replace(/<SIZE>/g, '400');

export const pipe = (...funcs) => (arg) => funcs.reduce((acc, cur) => cur(acc), arg);

/**
 * @param {Object} obj
 * @param {string} obj.subExperienceTag
 * @returns {boolean}
 */
export const isForwardsBogo = ({ subExperienceTag }) => {
  return FORWARDS_BOGO_SUBEXPERIENCES.some((subExperience) => subExperience === subExperienceTag);
};

/**
 * @param {Object} obj
 * @param {string} obj.subExperienceTag
 * @returns {boolean}
 */
export const isBackwardsBogo = ({ subExperienceTag }) => {
  return BACKWARDS_BOGO_SUBEXPERIENCES.some((subExperience) => subExperience === subExperienceTag);
};

/**
 * @param {Object} obj
 * @param {string} obj.subExperienceTag
 * @returns {boolean}
 */
export const isDollarThresholdBogo = ({ subExperienceTag }) => {
  return DOLLAR_THRESHOLD_BOGO_SUBEXPERIENCES.some((subExperience) => subExperience === subExperienceTag);
};

/**
 * @param {Object} conditionalPromotion
 * @returns {string[]}
 */
export const getSrc1ItemIdList = (conditionalPromotion) => {
  const src1ItemIds = conditionalPromotion.eligibilityCriteria?.find((eligibilityCriterion) => {
    return eligibilityCriterion.itemGroup === 'SRC-1';
  })?.omsSkus?.slice() || [];

  return src1ItemIds;
};

/**
 * @param {Object} conditionalPromotion
 * @returns {string[]}
 */
export const getTgt1ItemIdList = (conditionalPromotion) => {
  const tgt1ItemIds = conditionalPromotion.eligibilityCriteria?.find((eligibilityCriterion) => {
    return eligibilityCriterion.itemGroup === 'TGT-1';
  })?.omsSkus?.slice() || [];

  return tgt1ItemIds;
};

/**
 * applies business logic to determine the "first" and "second" lists.
 * @param {Object} anchorItem
 * @returns {{ firstListItemIds: string[], secondListItemIds: string[] }}
 */
export const getItemIdLists = (anchorItem) => {
  const conditionalPromotion = anchorItem.pricing.conditionalPromotions[0];
  const anchorItemFilter = (itemId) => (itemId !== anchorItem.itemId);
  let firstListItemIds = getSrc1ItemIdList(conditionalPromotion).filter(anchorItemFilter);
  let secondListItemIds = getTgt1ItemIdList(conditionalPromotion).filter(anchorItemFilter);

  if (isForwardsBogo(conditionalPromotion)) {
    firstListItemIds = secondListItemIds;
    secondListItemIds = [];
  } else if (isBackwardsBogo(conditionalPromotion)) {
    secondListItemIds = [];
  } else {
    // all non-simple BOGOs place the anchor at the top
    firstListItemIds = [
      anchorItem.itemId,
      ...firstListItemIds,
    ];
  }

  return {
    firstListItemIds,
    secondListItemIds,
  };
};

export const getProductImage = (productData) => productData.media?.image?.url || '';

/**
 *
 * @param {Object} obj
 * @param {boolean | null} [obj.prop]
 * @param {boolean | null} [obj.config]
 * @param {boolean} [obj.defaultValue]
 * @returns {boolean}
 */
export const getIsFeatureEnabled = ({ prop, config, defaultValue = false }) => {
  if (prop === false || config === false) return false;
  if (prop === true || config === true) return true;
  return Boolean(defaultValue);
};

/**
 * @param {Object} obj
 * @param {string | null} obj.experienceTag
 * @param {string | null} obj.subExperienceTag
 * @param {boolean | null} [obj.backwardsBogoEnabled] Backwards BOGO feature switch
 * @param {boolean | null} [obj.bogoDollarThresholdEnabled] BOGO dollar threshold feature switch
 * @param {boolean | null} [obj.bogoEnabled] Overall BOGO feature switch
 * @param {boolean | null} [obj.bmsmEnabled] BMSM feature switch
 * @param {boolean | null} [obj.msbEnabled] MSB feature switch
 * @returns {boolean} Whether the promotion is enabled
 */
export const isPromotionEnabled = ({
  experienceTag,
  subExperienceTag,
  backwardsBogoEnabled,
  bogoDollarThresholdEnabled,
  bogoEnabled,
  bmsmEnabled,
  msbEnabled,
  type
}) => {
  const isCard = type === 'card';
  switch (experienceTag) {
  case EXPERIENCE_TAGS.BOGO: {
    if (isForwardsBogo({ subExperienceTag })) {
      return Boolean(bogoEnabled);
    }

    if (isBackwardsBogo({ subExperienceTag })) {
      return Boolean(bogoEnabled && backwardsBogoEnabled && !isCard);
    }

    if (isDollarThresholdBogo({ subExperienceTag })) {
      return Boolean(bogoEnabled && bogoDollarThresholdEnabled && !isCard);
    }

    return false;
  }
  case EXPERIENCE_TAGS.BMSM:
    return Boolean(bmsmEnabled && !isCard);
  case EXPERIENCE_TAGS.MSB:
    return Boolean(msbEnabled && !isCard);
  default:
    return false;
  }
};
